import { EventEmitter, Injectable } from '@angular/core';
import { FileSetEntryViewModel, InstanceModel, StatePropertyValueModel } from '../../shared/fs.model';
import { LocalizePipe } from '@lf-web-components/lf-common-utils-library';
import { lfFilesetLocalize } from '../../shared/utils';

export class FileSetError {
  type: ErrorType;
  targetType: TargetType;
  target: string;
  message: string;

  constructor(
    type: ErrorType,
    targetType: TargetType,
    target: string,
    message?: string) {
      this.type = type;
      this.targetType = targetType;
      this.target = target;
      this.message = message ? message : "";
  }
}

export enum ErrorType {
  RequiredStatus = 0,
  MinCount = 1,
  MaxCount = 2
}

export enum TargetType {
  File = 0,
  FileType = 1
}

@Injectable()
export class ValidatorService {
  errors: FileSetError[] = [];
  types: StatePropertyValueModel[];
  statuses: StatePropertyValueModel[];
  requiredStatus: StatePropertyValueModel;
  validateStatus: boolean = true;

  public errorChanged: EventEmitter<FileSetError[]> = new EventEmitter<FileSetError[]>();

  constructor(
    private localizePipe: LocalizePipe
  ) {
  }

  setAndUpdateInstanceState(instanceState: InstanceModel, requiredStatusId: string, validateStatus: boolean): void {
    this.statuses = instanceState.State.StateProperties.find(p => p.StateKey === "status").StateProperties;
    this.types = instanceState.State.StateProperties.find(p => p.StateKey === "type").StateProperties;
    this.requiredStatus = this.statuses.find(status => status.Key === requiredStatusId);
    this.validateStatus = validateStatus;
  }

  private addError(type: ErrorType, targetType: TargetType, target: string, message: string): void {
    const idx = this.errors.findIndex(e => e.type === type 
      && e.targetType === targetType 
      && e.target === target);
    
    if (idx < 0) { // check error doesn't already exist
      this.errors.push(new FileSetError(type, targetType, target, message));
      this.errorChanged.emit(this.errors);
    }
  }

  removeError(type: ErrorType, targetType: TargetType, target: string): void {
    const idx = this.errors.findIndex(e => e.type === type 
      && e.targetType === targetType 
      && e.target === target);
    
    if (idx > -1) { // check error exists
      this.errors.splice(idx, 1);
      this.errorChanged.emit(this.errors);
    }
  }

  private resolveTypeError(type: StatePropertyValueModel, files: FileSetEntryViewModel[]): void {
    let count = files.filter(f => f.docType === type.Key).length;

    if (count < type.Settings["min"]) {
      this.addError(ErrorType.MinCount, TargetType.FileType, type.Key, this.localizePipe.transform({ key: 'lf_fileset_file_type_min_error', args: [type.DisplayName, type.Settings["min"]] }, lfFilesetLocalize));
    } else {
      this.removeError(ErrorType.MinCount, TargetType.FileType, type.Key);
    }
    
    if (count > type.Settings["max"]) {
      this.addError(ErrorType.MaxCount, TargetType.FileType, type.Key, this.localizePipe.transform({ key: 'lf_fileset_file_type_max_error', args: [type.DisplayName, type.Settings["max"]] }, lfFilesetLocalize));
    } else {
      this.removeError(ErrorType.MaxCount, TargetType.FileType, type.Key);
    }
  }

  private resolveFileError(file: FileSetEntryViewModel): void {
    if (this.requiredStatus && this.validateStatus) {
      if (file.status !== this.requiredStatus.Key) {
        this.addError(ErrorType.RequiredStatus, TargetType.File, file.id, this.localizePipe.transform({ key: 'lf_fileset_required_status_error', args: [this.requiredStatus.DisplayName, file.name] }, lfFilesetLocalize));
      } else {
        this.removeError(ErrorType.RequiredStatus, TargetType.File, file.id);
      }
    }
  }

  validateFileType(typeId: string, files: FileSetEntryViewModel[]): void {
    if (this.types) {
      let type = this.types.find(t => t.Key === typeId);

      if (type) {
        this.resolveTypeError(type, files);
      }
    }
  }

  validateFileTypes(files: FileSetEntryViewModel[]): void {
    if (this.types) {
      this.types.forEach((type) => {
        this.resolveTypeError(type, files);
      });
    }
  }

  validateFile(file: FileSetEntryViewModel): void {
    this.resolveFileError(file);
  }

  validateDuplicateFiles(files: FileSetEntryViewModel[]): void {
    if (this.requiredStatus && this.validateStatus && files.length) {
      let addError = false;
      files.forEach(file => {
        if (file.status !== this.requiredStatus.Key) {
          addError = true;
        }
      });

      if (addError) {
        this.addError(ErrorType.RequiredStatus, TargetType.File, files[0].id, this.localizePipe.transform({ key: 'lf_fileset_required_status_error', args: [this.requiredStatus.DisplayName, files[0].name] }, lfFilesetLocalize));
      } else {
        this.removeError(ErrorType.RequiredStatus, TargetType.File, files[0].id);
      }
    } 
  }

  validateInstanceData(files: FileSetEntryViewModel[]): void {
    this.validateFileTypes(files);

    if (this.requiredStatus && this.validateStatus) {
      files.forEach((file) => {
        this.resolveFileError(file);
      });
    }
  }

}
